import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { toast } from "sonner";
import { MagnifyingGlass, PawPrint } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

/**
 * Patient-selection popup for creating a new consultation. The user must
 * explicitly search for and pick a patient — a consultation is created only
 * after a patient is confirmed. Closing without selecting creates nothing.
 */
export default function NewConsultationDialog({ open, onOpenChange }) {
  const { t } = useLang();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const debounce = useRef(null);

  useEffect(() => {
    if (!open) {
      setQ("");
      setResults([]);
      return;
    }
    // Load an initial (recent) list when opened.
    fetchPatients("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  async function fetchPatients(keyword) {
    setLoading(true);
    try {
      const { data } = await api.get("/patients", {
        params: keyword ? { q: keyword, limit: 20 } : { limit: 20 },
      });
      setResults(data);
    } catch (e) {
      /* silent */
    } finally {
      setLoading(false);
    }
  }

  function onSearch(v) {
    setQ(v);
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => fetchPatients(v.trim()), 250);
  }

  async function pick(patient) {
    if (creating) return;
    setCreating(true);
    try {
      const { data } = await api.post("/consultations", { patient_id: patient.id });
      onOpenChange(false);
      navigate(`/consultations/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
      setCreating(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="new-consult-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{t("newConsultation")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-pf-text-secondary">{t("selectPatientForConsult")}</p>
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-pf-text-secondary" />
            <input
              autoFocus
              data-testid="new-consult-search"
              className="pf-input pl-9"
              placeholder={t("searchPlaceholder")}
              value={q}
              onChange={(e) => onSearch(e.target.value)}
            />
          </div>
          <div className="max-h-80 overflow-y-auto -mx-1 px-1 space-y-1" data-testid="new-consult-results">
            {loading ? (
              <div className="text-sm text-pf-text-secondary text-center py-6">…</div>
            ) : results.length === 0 ? (
              <div className="text-sm text-pf-text-secondary text-center py-6">{t("noResults")}</div>
            ) : (
              results.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  disabled={creating}
                  onClick={() => pick(p)}
                  data-testid={`new-consult-patient-${p.id}`}
                  className="w-full flex items-center gap-3 text-left rounded-lg px-3 py-2.5 hover:bg-pf-primary-light transition-colors disabled:opacity-50"
                >
                  <PawPrint size={18} weight="fill" className="text-pf-primary shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-pf-text truncate">{p.name}</div>
                    <div className="text-xs text-pf-text-secondary truncate">
                      {[p.species, p.breed, p.owner?.full_name].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
